import React from 'react';
import { Button, Card, CardBody } from '@heroui/react';
import { Icon } from '@iconify/react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <Card className="my-8 mx-auto max-w-md">
          <CardBody className="p-6 text-center">
            <Icon icon="lucide:alert-triangle" className="mx-auto mb-4 text-danger" width={48} />
            <h3 className="text-lg font-medium mb-2">Что-то пошло не так</h3>
            <p className="text-foreground/70 mb-6">
              {this.state.error?.message || 'Произошла ошибка при загрузке данных.'}
            </p>
            <Button 
              color="primary"
              variant="flat"
              onPress={() => this.setState({ hasError: false, error: null })}
            >
              Попробовать снова
            </Button>
          </CardBody>
        </Card>
      );
    }

    return this.props.children;
  }
}

/**
 * Component for displaying an error state
 */
export const ErrorDisplay = ({ 
  message = 'Произошла ошибка при загрузке данных.', 
  onRetry 
}: { 
  message?: string;
  onRetry?: () => void;
}) => {
  return (
    <Card className="my-8 mx-auto max-w-md">
      <CardBody className="p-6 text-center">
        <Icon icon="lucide:alert-triangle" className="mx-auto mb-4 text-danger" width={48} />
        <h3 className="text-lg font-medium mb-2">Не удалось загрузить данные</h3>
        <p className="text-foreground/70 mb-6">{message}</p>
        {onRetry && (
          <Button 
            color="primary"
            variant="flat"
            onPress={onRetry}
          >
            Попробовать снова
          </Button>
        )}
      </CardBody>
    </Card>
  );
};

/**
 * Component for displaying a loading state
 */
export const LoadingDisplay = ({ 
  message = 'Загрузка данных...',
}: { 
  message?: string;
}) => {
  return (
    <div className="my-8 flex flex-col items-center justify-center">
      <div className="mb-4">
        <div className="w-16 h-16">
          <svg className="animate-spin text-primary" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
      <p className="text-foreground/70">{message}</p>
    </div>
  );
};
